import React from 'react';
import { useSelector } from 'react-redux';
import { AEOffering, AEPlanType, AETiers, AnnualEnrollmentState } from '../../models/annual-enrollment-models';
import { AnnualEnrollmentProps } from '../../../arc/models/arc-epi-props';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { getTierLabel, isPlanSelectionAvailable } from '../../helpers/annual-enrollment-helpers';
import { getCurrencyFormat } from '../../../../global/helpers/format-helpers';
import { TcsmStates } from '../../../../root/tcsm-model';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { DeclinedValue } from '../../../arc/features/new-hire-enrollment/enrollment/models/coverage-models';

interface PlanDisplay {
	name: string;
	tier: AETiers;
	price: number;
}

interface Props {
	aeState: AnnualEnrollmentState;
	medicalPlan: string;
	dentalPlan: string;

	annualEnrollmentProps: AnnualEnrollmentProps;
	editButton: JSX.Element;
	medicalExpired: boolean,
	dentalExpired: boolean,
}

export function AnnualEnrollmentPlansDisplay(props: Props) {
	const { medicalPlan, dentalPlan, annualEnrollmentProps, editButton, aeState, medicalExpired, dentalExpired } = props;

	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps.labelSettingState.epiContent);


	const getPlanDisplay = (planType: AEPlanType): PlanDisplay => {

		const selectedPlan = planType === AEPlanType.Medical ? medicalPlan : dentalPlan;

		if (!(aeState?.AEInfo?.offerings[planType]?.plans?.length > 0)) {
			return null;
		}

        if (selectedPlan === DeclinedValue) {
            return {
                name: planType === AEPlanType.Medical ? annualEnrollmentProps?.declinedMedicalCoverageReviewText : annualEnrollmentProps?.declinedDentalCoverageReviewText,
                tier: null,
                price: null,
            };
        }

		const matchedPlan = (aeState?.AEInfo?.offerings[planType] as AEOffering)?.plans?.filter(plan => plan.code === selectedPlan)[0];


		const tiers = planType === AEPlanType.Medical ? aeState?.tierCalculations.M : aeState?.tierCalculations.D;

		return {
			name: matchedPlan?.name,
			tier: tiers.display_tier,
			price: matchedPlan?.rates_by_tier[tiers.selected_tier],
		};

	};

	const renderPlanDisplay = (info: PlanDisplay, hideRate: boolean) => {
		if (!info) return null;

		return (
			<div>
				<h5>{info.name}</h5>
				{info.tier && !hideRate && <p className='input-caption'>{getTierLabel(info.tier, annualEnrollmentProps)}</p>}
				<p>{!hideRate && getCurrencyFormat(info.price, labelSetting, true)}</p>
			</div>
		);
	};

	const medicalPlanDisplay = getPlanDisplay(AEPlanType.Medical);
	const dentalPlanDisplay = getPlanDisplay(AEPlanType.Dental);

	return (
		<DividerSection removeDivider sectionText={annualEnrollmentProps?.planSelectionLabel} secondarySectionNode={editButton}>
			{
				isPlanSelectionAvailable(aeState?.AEInfo?.offerings?.M?.plans, medicalExpired) && (
					<div className='divider-section-item'>
						<h4>{annualEnrollmentProps?.medicalPlanLabel}</h4>
						{renderPlanDisplay(medicalPlanDisplay, !medicalPlan)}
					</div>
				)
			}
			{
				isPlanSelectionAvailable(aeState?.AEInfo?.offerings?.D?.plans, dentalExpired) && (
					<div className='divider-section-item'>
						<h4>{annualEnrollmentProps?.dentalPlanLabel}</h4>
						{renderPlanDisplay(dentalPlanDisplay, !dentalPlan)}
					</div>
				)
			}

		</DividerSection>
	);
}